import { useState, useCallback, useRef } from 'react';
import { SAPContactSearchState } from '../types';
import { isSapC4cEnabled } from '../utils/config';
import { contactsApi } from '../services/api';
import { logCallActivity } from '../services/call-log/call-log.service';
import { toCallLogContactSummaries } from '../services/call-log/call-log-contact.util';
import { logger } from '../utils/logger';
import { normalizeSearchKey } from './sap-contact/normalize-search-key.util';

interface UseSAPContactSearchReturn {
  contactState: SAPContactSearchState;
  searchContacts: (query: string) => Promise<void>;
  clearContacts: () => void;
  isEnabled: boolean;
}

export function useSAPContactSearch(): UseSAPContactSearchReturn {
  const isEnabled = isSapC4cEnabled();

  const [contactState, setContactState] = useState<SAPContactSearchState>({
    contacts: [],
    isLoading: false,
    error: null,
  });

  const loadedQueryRef = useRef<string | null>(null);
  const inFlightQueryRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const searchContacts = useCallback(async (query: string): Promise<void> => {
    if (!isSapC4cEnabled()) {
      logger.debug('[SAP C4C] SAP C4C integration is disabled, skipping search');
      return;
    }

    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      logger.debug('[SAP C4C] No search query provided, skipping search');
      return;
    }

    const searchKey = normalizeSearchKey(trimmedQuery);
    if (loadedQueryRef.current === searchKey) {
      logger.debug('[SAP C4C] Skipping duplicate contact search', { query: searchKey });
      return;
    }

    if (inFlightQueryRef.current === searchKey) {
      logger.debug('[SAP C4C] Contact search already in flight', { query: searchKey });
      return;
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    inFlightQueryRef.current = searchKey;

    setContactState({ contacts: [], isLoading: true, error: null });

    try {
      logger.debug('[SAP C4C] Searching contacts', { query: trimmedQuery });
      const contacts = await contactsApi.search(trimmedQuery, controller.signal);

      if (controller.signal.aborted) {
        return;
      }

      loadedQueryRef.current = searchKey;
      setContactState({ contacts, isLoading: false, error: null });
      logCallActivity('SAP_CONTACTS_DISPLAYED', {
        source: 'query',
        query: searchKey,
        contactCount: contacts.length,
        contacts: toCallLogContactSummaries(contacts),
      });
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }

      logger.error('[SAP C4C] Failed to search contacts', error);
      setContactState({
        contacts: [],
        isLoading: false,
        error: (error as Error).message || 'Failed to search SAP contacts',
      });
    } finally {
      if (abortControllerRef.current === controller) {
        inFlightQueryRef.current = null;
      }
    }
  }, []);

  const clearContacts = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    loadedQueryRef.current = null;
    inFlightQueryRef.current = null;
    setContactState({ contacts: [], isLoading: false, error: null });
  }, []);

  return { contactState, searchContacts, clearContacts, isEnabled };
}

export default useSAPContactSearch;
